import { useState, useRef, useEffect, useMemo } from "react";
import { Play, Pause, SkipBack, SkipForward, ChevronLeft, ChevronRight } from "lucide-react";

const VideoPlayer = ({ videoUrl, analysisData }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showOverlays, setShowOverlays] = useState(true);
  const [currentPhase, setCurrentPhase] = useState("setup");
  const [playbackRate, setPlaybackRate] = useState(1);

  const swingPhases = useMemo(() => {
    const timestamps = analysisData?.phase_timestamps || {};
    const videoEnd = duration || analysisData?.stored_duration || timestamps.follow_through_end || 1;
    const setup = Math.max(0, timestamps.setup_start ?? 0);
    const backswing = Math.max(setup, timestamps.backswing_start ?? setup);
    const top = Math.max(backswing, timestamps.top_of_swing ?? backswing);
    const downswing = Math.max(top, timestamps.downswing_start ?? top);
    const impact = Math.max(downswing, timestamps.impact ?? downswing);

    return [
      { id: "setup", name: "Setup", start: setup, end: backswing, color: "#A3A3A3" },
      { id: "backswing", name: "Backswing", start: backswing, end: top, color: "#CCFF00" },
      { id: "top", name: "Top", start: top, end: downswing, color: "#FFD60A" },
      { id: "downswing", name: "Downswing", start: downswing, end: impact, color: "#FF6B00" },
      { id: "impact", name: "Impact", start: impact, end: impact, color: "#FF3B30" },
      { id: "follow", name: "Follow-Through", start: impact, end: videoEnd, color: "#00C853" },
    ];
  }, [analysisData, duration]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      const phase = Math.abs(video.currentTime - (analysisData?.phase_timestamps?.impact ?? -1)) < 0.08
        ? swingPhases.find((item) => item.id === "impact")
        : swingPhases.find((item) => item.id !== "impact" && video.currentTime >= item.start && video.currentTime < item.end);
      if (phase) setCurrentPhase(phase.id);
    };

    const handleLoadedMetadata = () => setDuration(video.duration);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);

    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("ended", handleEnded);

    return () => {
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("ended", handleEnded);
    };
  }, [analysisData, swingPhases]);

  const togglePlay = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const video = videoRef.current;
    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  };

  const skipFrames = (frames) => {
    const video = videoRef.current;
    video.currentTime = Math.max(0, Math.min(duration, video.currentTime + (frames / 30)));
  };

  const seekToPhase = (phase) => {
    const video = videoRef.current;
    video.currentTime = phase.start;
  };

  const changeSpeed = (speed) => {
    const video = videoRef.current;
    video.playbackRate = speed;
    setPlaybackRate(speed);
  };

  const formatTime = (time) => {
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    const ms = Math.floor((time % 1) * 100);
    return `${mins}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`;
  };

  const progressPercentage = duration ? (currentTime / duration) * 100 : 0;
  const activePhase = swingPhases.find(p => p.id === currentPhase);

  return (
    <div className="bg-[#121212] border border-[#262626] rounded-sm overflow-hidden" data-testid="video-player">
      {/* Video Container */}
      <div className="relative aspect-video bg-black">
        <video
          ref={videoRef}
          src={videoUrl}
          className="w-full h-full object-contain"
          playsInline
          muted={false}
          preload="auto"
        />

        {/* Phase Indicator Overlay */}
        {showOverlays && activePhase && (
          <div 
            className="absolute top-3 left-3 px-3 py-1 rounded-sm text-xs font-mono uppercase tracking-wider pointer-events-none"
            style={{ backgroundColor: activePhase.color + "33", color: activePhase.color, border: `1px solid ${activePhase.color}` }}
          >
            {activePhase.name}
          </div>
        )}

        {/* Swing Path Overlay */}
        {showOverlays && (
          <div className="absolute top-3 right-3 bg-black/70 px-3 py-1 rounded-sm pointer-events-none">
            <p className="text-xs font-mono text-[#A3A3A3]">Path: <span className="text-[#CCFF00]">{analysisData?.swing_path_status}</span></p>
          </div>
        )}

        {/* Play/Pause Overlay - clickable */}
        {!isPlaying && (
          <button 
            className="absolute inset-0 flex items-center justify-center bg-black/30"
            onClick={togglePlay}
            type="button"
          >
            <div className="w-16 h-16 rounded-full bg-[#CCFF00] flex items-center justify-center">
              <Play className="w-8 h-8 text-black ml-1" />
            </div>
          </button>
        )}

        {/* Tap to pause when playing */}
        {isPlaying && (
          <button 
            className="absolute inset-0"
            onClick={togglePlay}
            type="button"
          />
        )}

        {/* Speed Indicator */}
        {playbackRate !== 1 && (
          <div className="absolute bottom-3 right-3 bg-black/70 px-2 py-1 rounded-sm pointer-events-none">
            <p className="text-xs font-mono text-[#CCFF00]">{playbackRate}x</p>
          </div>
        )}
      </div>

      {/* Phase Timeline */}
      <div className="h-2 flex">
        {swingPhases.map((phase) => (
          <div
            key={phase.id}
            className="h-full cursor-pointer transition-opacity hover:opacity-80"
            style={{
              width: phase.id === "impact"
                ? "1%"
                : `${Math.max(0, ((phase.end - phase.start) / (duration || 1)) * 100)}%`,
              backgroundColor: phase.color,
              opacity: currentPhase === phase.id ? 1 : 0.4,
            }}
            onClick={() => seekToPhase(phase)}
            title={phase.name}
          />
        ))}
      </div>

      {/* Progress Bar */}
      <div className="h-1 bg-[#262626] relative cursor-pointer" onClick={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const percentage = (e.clientX - rect.left) / rect.width;
        videoRef.current.currentTime = percentage * duration;
      }}>
        <div 
          className="h-full bg-[#CCFF00] transition-all"
          style={{ width: `${progressPercentage}%` }}
        />
      </div>

      {/* Controls */}
      <div className="p-3 flex items-center justify-between gap-2 flex-wrap">
        {/* Time */}
        <div className="font-mono text-xs text-[#A3A3A3]">
          {formatTime(currentTime)} / {formatTime(duration)}
        </div>

        {/* Playback Controls */}
        <div className="flex items-center gap-1">
          <button onClick={() => skipFrames(-10)} className="p-1.5 hover:bg-[#262626] rounded" title="Back 10 frames">
            <SkipBack className="w-4 h-4 text-[#A3A3A3]" />
          </button>
          <button onClick={() => skipFrames(-1)} className="p-1 hover:bg-[#262626] rounded" title="Previous frame">
            <ChevronLeft className="w-4 h-4 text-[#A3A3A3]" />
          </button>
          <button onClick={togglePlay} className="p-2 bg-[#CCFF00] hover:bg-white rounded">
            {isPlaying ? <Pause className="w-4 h-4 text-black" /> : <Play className="w-4 h-4 text-black ml-0.5" />}
          </button>
          <button onClick={() => skipFrames(1)} className="p-1 hover:bg-[#262626] rounded" title="Next frame">
            <ChevronRight className="w-4 h-4 text-[#A3A3A3]" />
          </button>
          <button onClick={() => skipFrames(10)} className="p-1.5 hover:bg-[#262626] rounded" title="Forward 10 frames">
            <SkipForward className="w-4 h-4 text-[#A3A3A3]" />
          </button>
        </div>

        {/* Speed & Overlay */}
        <div className="flex items-center gap-2">
          <div className="flex bg-[#1A1A1A] rounded overflow-hidden">
            {[0.25, 0.5, 1].map((speed) => (
              <button
                key={speed}
                onClick={() => changeSpeed(speed)}
                className={`px-2 py-1 text-xs font-mono ${playbackRate === speed ? "bg-[#CCFF00] text-black" : "text-[#A3A3A3]"}`}
              >
                {speed}x
              </button>
            ))}
          </div>
          <button
            onClick={() => setShowOverlays(!showOverlays)}
            className={`px-2 py-1 text-xs font-mono rounded ${showOverlays ? "bg-[#CCFF00]/20 text-[#CCFF00]" : "bg-[#1A1A1A] text-[#A3A3A3]"}`}
          >
            Overlays
          </button>
        </div>
      </div>

      {/* Phase Jump Buttons */}
      <div className="px-3 pb-3 flex gap-1 flex-wrap">
        {swingPhases.map((phase) => (
          <button
            key={phase.id}
            onClick={() => seekToPhase(phase)}
            className={`px-2 py-1 text-[10px] font-mono uppercase rounded transition-all ${
              currentPhase === phase.id ? "text-black" : "text-[#A3A3A3] bg-[#1A1A1A]"
            }`}
            style={currentPhase === phase.id ? { backgroundColor: phase.color } : {}}
          >
            {phase.name}
          </button>
        ))}
      </div>
    </div>
  );
};

export default VideoPlayer;
